import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import {
  concatMap, map, take
} from 'rxjs/operators';

import {
  mapAssignmentFullid, mapAssignmentToValueText,
  mapMouseoverAssignmentToValueText, mapMouseoverToValueText
} from 'app/utils/map-assignments';
import {
  ProductAssignment,
  AppProductLine as ProductLine,
  SnkProduct
} from 'graphql/models/extended';
import { ValueTextsQuery } from 'graphql/queries/value-texts';
import {
  ValueText, ValueTextInput, ValueTextsStorage,
  ValueTextType, GroupedMouseoverText, MouseoverTextType
} from 'models/value-text.model';
import { StoreService } from 'shared/services/store.service';

@Injectable({
  providedIn: 'root'
})
export class ValueTextsService {
  constructor(
    private storeService: StoreService,
    private translateService: TranslateService,
    private valueTextsQuery: ValueTextsQuery
  ) { }

  mapValueTextsToProductLine(pos: ProductLine, type: ValueTextType): Observable<ProductLine> {
    let allAssignments: ProductAssignment[] = [];
    allAssignments = allAssignments.concat(pos.product.productAssignments || []);
    allAssignments = allAssignments.concat(pos.product.positionAssignments || []);

    return this.getValueTexts(allAssignments, type).pipe(
      map((storagevalueTexts: ValueTextsStorage) => this.mapStorageValueTextsToPosition(
        pos,
        storagevalueTexts,
        type
      ))
    );
  }

  getMouseoverTexts(
    assignments: ValueTextInput[],
    type: MouseoverTextType
  ): Observable<ValueTextsStorage> {
    return this.storeService.getMouseoverTexts()
      .pipe(
        concatMap((valueTextsStorage: ValueTextsStorage) => {
          const storageValuesTexts = { ...valueTextsStorage };
          let valueTextInput: ValueTextInput[] = [];

          if (!storageValuesTexts[type]) {
            storageValuesTexts[type] = {};
          }

          if(assignments) {
            valueTextInput = assignments.map((a) => mapMouseoverToValueText(a as ValueText)).filter((val) => storageValuesTexts[type][val.fullId] === undefined);

            if (type === MouseoverTextType.Configuration) {
              const miniCartDetailsMouseovers = assignments.map(
                (assignment) => mapMouseoverAssignmentToValueText(assignment as ValueText)
              ).filter((val) => storageValuesTexts[type][val.fullId] === undefined);

              valueTextInput = valueTextInput.concat(miniCartDetailsMouseovers);
            }
          }

          if (valueTextInput.length > 0) {
            return this.queryValueTexts(
              ValueTextType.Mouseover,
              valueTextInput
            ).pipe(
              map((valueTexts: ValueText[]) => {
                const sValuesTexts = { ...storageValuesTexts };
                valueTexts.forEach((vt: ValueText) => {
                  if (vt.text) {
                    sValuesTexts[type] = { ...sValuesTexts[type], [vt.fullId]: vt.text };
                  }
                });

                this.storeService.setMouseoverTexts(sValuesTexts);

                return sValuesTexts;
              })
            );
          }

          return of(storageValuesTexts);
        }),
        take(1)
      );
  }

  getGroupedMouseoverTexts(
    assignments: ValueTextInput[]
  ): Observable<GroupedMouseoverText> {
    return this.storeService.getGroupedMouseoverTexts()
      .pipe(
        concatMap((valueTextsStorage: GroupedMouseoverText) => {
          let storageValuesTexts = { ...valueTextsStorage };
          let valueTextInput: ValueTextInput[] = [];

          if (!storageValuesTexts) {
            storageValuesTexts = {};
          }

          valueTextInput = (assignments as ValueTextInput[]);

          if (valueTextInput.length > 0) {
            return this.queryValueTexts(
              ValueTextType.Mouseover,
              valueTextInput
            ).pipe(
              map((valueTexts: ValueText[]) => {
                const groupedTxts: any = valueTexts.reduce((acc: GroupedMouseoverText, i: ValueText) => {
                  if (i.text) {
                    ((acc[i.fullId] = acc[i.fullId] || []) as string[]).push(i.text);
                  }

                  return acc;
                }, {});

                valueTexts.forEach((vt: ValueText) => {
                  storageValuesTexts[vt.fullId] = groupedTxts[vt.fullId];
                });

                this.storeService.setGroupedMouseoverTexts(storageValuesTexts);

                return storageValuesTexts;
              })
            );
          }

          return of(storageValuesTexts);
        }),
        take(1)
      );
  }

  getValueTexts(
    assignments: ProductAssignment[],
    type: ValueTextType
  ): Observable<ValueTextsStorage> {
    return this.storeService.getValueTexts()
      .pipe(
        concatMap((valueTextsStorage: ValueTextsStorage) => {
          const storageValuesTexts = { ...valueTextsStorage };

          if (!storageValuesTexts[type]) {
            storageValuesTexts[type] = {};
          }

          const valueTextInput: ValueTextInput[] = this.prepareValueTextsForQuery(
            assignments,
            storageValuesTexts,
            type
          );

          if (valueTextInput.length > 0) {
            return this.queryValueTexts(
              type,
              valueTextInput
            ).pipe(
              map((valueTexts: ValueText[]) => {
                const sValuesTexts = { ...storageValuesTexts };
                valueTexts.forEach((vt: ValueText) => {
                  sValuesTexts[type] = { ...sValuesTexts[type], [vt.fullId]: vt.text };
                });
                this.storeService.setValueTexts(sValuesTexts);

                return sValuesTexts;
              })
            );
          }

          return of(storageValuesTexts);
        }),
        take(1)
      );
  }

  mapStorageValueTextsToPosition(
    pos: ProductLine,
    storageValueTexts: ValueTextsStorage,
    type: ValueTextType
  ): ProductLine {
    const position = { ...pos };

    if ('product' in position && position.product) {

      const product = { ...position.product } as SnkProduct;
      product.productAssignments = this.mapStorageValueTextsToAssignments(
        position.product.productAssignments,
        storageValueTexts,
        type
      );

      product.positionAssignments = this.mapStorageValueTextsToAssignments(
        position.product.positionAssignments,
        storageValueTexts,
        type
      );

      return { ...position, product };
    }

    return position;
  }

  mapStorageValueTextsToAssignments(
    assignments: ProductAssignment[],
    storageValueTexts: ValueTextsStorage,
    type: ValueTextType
  ): ProductAssignment[] {
    return (assignments || []).map((a) => ({
      ...a,
      text: (storageValueTexts[type] && storageValueTexts[type][mapAssignmentFullid(a)]) || ''
    }));
  }

  private prepareValueTextsForQuery(
    pAssignments: ProductAssignment[],
    storageValueTexts: ValueTextsStorage,
    type: ValueTextType
  ): ValueTextInput[] {
    return pAssignments.map((val) => mapAssignmentToValueText(val))
      .filter((val) => storageValueTexts[type][val.fullId] === undefined);
  }

  queryValueTexts(type: ValueTextType, values: ValueTextInput[]): Observable<ValueText[]> {
    const language = this.translateService.currentLang || this.translateService.defaultLang;
    const variables = {
      language,
      type,
      values
    };

    return this.valueTextsQuery.fetch(variables, { fetchPolicy: 'no-cache' })
      .pipe<ValueText[], ValueText[]>(
        map((r) => r.data?.valueTexts?.values as ValueText[]),
        take(1)
      );
  }
}
